/**
 * Base HTTP client for all Vesant SDK clients
 *
 * Provides common functionality:
 * - Request/response handling
 * - Error handling
 * - Retry logic with exponential backoff
 * - Timeout management
 * - Debug logging
 */

import type { BaseClientConfig, Logger, RequestInterceptor, RequestOptions } from './config';
import {
  VesantError,
  NetworkError,
  TimeoutError,
  AuthenticationError,
  RateLimitError,
  ServiceUnavailableError,
  ValidationError,
  CircuitBreakerOpenError,
} from './errors';
import { CircuitBreaker, type CircuitBreakerStatus } from './circuit-breaker';
import { RateLimitTracker, type RateLimitStatus } from './rate-limiter';
import { createConsoleLogger } from './logger';
import { SDK_VERSION } from './version';
import { generateUUID } from '../shared/browser-utils';

export abstract class BaseClient {
  protected config: BaseClientConfig & { timeout: number; retries: number };
  protected logger: Logger;
  private interceptors: RequestInterceptor[];
  private circuitBreaker: CircuitBreaker | null = null;
  private rateLimitTracker: RateLimitTracker | null = null;

  constructor(config: BaseClientConfig) {
    if (!config.baseURL?.trim()) {
      throw new ValidationError('baseURL is required and must be a non-empty string', ['baseURL']);
    }
    if (!config.tenantId?.trim()) {
      throw new ValidationError('tenantId is required and must be a non-empty string', ['tenantId']);
    }

    this.interceptors = config.interceptors || [];
    this.logger = config.logger || createConsoleLogger();

    // Auto-detect environment from API key prefix
    let environment = config.environment;
    const apiKey = config.apiKey || '';
    if (apiKey.startsWith('pk_test_')) {
      if (environment === 'production') {
        this.logger.warn('Sandbox API key (pk_test_*) used with environment: "production" — overriding to "sandbox"');
      }
      environment = 'sandbox';
    } else if (apiKey.startsWith('pk_live_') && environment === 'sandbox') {
      this.logger.warn('Production API key (pk_live_*) used with environment: "sandbox" — sandbox isolation will still be applied for backward compatibility');
    }

    this.config = {
      ...config,
      apiKey,
      environment,
      headers: config.headers || {},
      timeout: config.timeout || 10000,
      retries: config.retries || 3,
      debug: config.debug || false,
      interceptors: this.interceptors,
      logger: this.logger,
    };

    if (config.circuitBreaker) {
      this.circuitBreaker = new CircuitBreaker(config.circuitBreaker);
    }
    if (config.enableRateLimitTracking) {
      this.rateLimitTracker = new RateLimitTracker();
    }
  }

  /**
   * Make an HTTP request with timeout and error handling
   */
  protected async request<T>(
    endpoint: string,
    options: RequestInit = {},
    serviceURL?: string,
    requestOptions?: RequestOptions
  ): Promise<T> {
    const requestId = generateUUID();

    // Circuit breaker check
    if (this.circuitBreaker && !this.circuitBreaker.canExecute()) {
      const error = new CircuitBreakerOpenError();
      error.requestId = requestId;
      throw error;
    }

    // Rate limit pre-check
    if (this.rateLimitTracker && this.rateLimitTracker.isLimitExceeded()) {
      const status = this.rateLimitTracker.getStatus();
      const error = new RateLimitError(status.retryAfter ?? undefined);
      error.requestId = requestId;
      throw error;
    }

    const url = `${serviceURL || this.config.baseURL}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-ID': this.config.tenantId,
      'X-SDK-Version': `vesant-sdk-ts/${SDK_VERSION}`,
      'X-Request-ID': requestId,
      ...this.config.headers,
      ...((options.headers as Record<string, string>) || {}),
    };

    if (this.config.apiKey) {
      headers['Authorization'] = `Bearer ${this.config.apiKey}`;
    }

    // Sandbox header
    if (this.config.environment === 'sandbox') {
      headers['X-Sandbox'] = 'true';
    }

    // Idempotency key for mutating methods
    const method = (options.method || 'GET').toUpperCase();
    if (['POST', 'PUT', 'PATCH'].includes(method)) {
      headers['Idempotency-Key'] = requestOptions?.idempotencyKey || generateUUID();
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    // Link consumer AbortSignal to internal controller
    if (requestOptions?.signal) {
      if (requestOptions.signal.aborted) {
        controller.abort();
      } else {
        requestOptions.signal.addEventListener('abort', () => controller.abort(), { once: true });
      }
    }

    try {
      // Run onRequest interceptors
      let finalOptions: RequestInit = { ...options, headers };
      for (const interceptor of this.interceptors) {
        if (interceptor.onRequest) {
          finalOptions = await interceptor.onRequest(url, finalOptions);
        }
      }

      if (this.config.debug) {
        this.logger.debug(`${finalOptions.method || 'GET'} ${endpoint}`);
      }

      const response = await fetch(url, {
        ...finalOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Track rate limit headers
      if (this.rateLimitTracker) {
        this.rateLimitTracker.updateFromHeaders(response.headers);
      }

      // Binary download path
      if (requestOptions?.responseType === 'arraybuffer') {
        if (!response.ok) {
          let errData: Record<string, unknown> = {};
          try { errData = await response.json(); } catch { /* ignore */ }
          this.handleErrorResponse(response.status, errData, requestId);
        }
        this.circuitBreaker?.onSuccess();
        const buffer = await response.arrayBuffer();
        return buffer as unknown as T;
      }

      let data: Record<string, unknown> | undefined;
      try {
        data = await response.json();
      } catch {
        // Non-JSON response (HTML error pages, 204 No Content, etc.)
        if (!response.ok) {
          this.handleErrorResponse(response.status, {
            error: `HTTP ${response.status}`,
            message: response.statusText,
          }, requestId);
        }
        // OK response but no JSON body (e.g., 204)
        this.circuitBreaker?.onSuccess();
        return undefined as unknown as T;
      }

      if (!response.ok) {
        this.handleErrorResponse(response.status, data || {}, requestId);
      }

      // Record success for circuit breaker
      this.circuitBreaker?.onSuccess();

      // Run onResponse interceptors
      let result: unknown = data;
      for (const interceptor of this.interceptors) {
        if (interceptor.onResponse) {
          result = await interceptor.onResponse(url, result);
        }
      }

      if (this.config.debug) {
        this.logger.debug(`Response: ${response.status}`);
      }

      return result as T;
    } catch (error) {
      clearTimeout(timeoutId);

      // Record failure for circuit breaker (skip for client errors)
      if (
        error instanceof VesantError &&
        error.statusCode &&
        error.statusCode >= 500
      ) {
        this.circuitBreaker?.onFailure();
      } else if (error instanceof NetworkError || error instanceof TimeoutError) {
        this.circuitBreaker?.onFailure();
      }

      // Attach requestId to VesantError instances
      if (error instanceof VesantError && !error.requestId) {
        error.requestId = requestId;
      }

      // Run onError interceptors
      if (error instanceof Error) {
        for (const interceptor of this.interceptors) {
          if (interceptor.onError) {
            await interceptor.onError(url, error);
          }
        }
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          // Distinguish consumer abort from timeout
          if (requestOptions?.signal?.aborted) {
            const abortError = new VesantError('Request aborted', 'REQUEST_ABORTED');
            abortError.requestId = requestId;
            throw abortError;
          }
          this.circuitBreaker?.onFailure();
          const timeoutError = new TimeoutError(this.config.timeout);
          timeoutError.requestId = requestId;
          throw timeoutError;
        }
        if (error instanceof VesantError) {
          throw error;
        }
      }

      const networkError = new NetworkError('Network request failed', error);
      networkError.requestId = requestId;
      this.circuitBreaker?.onFailure();
      throw networkError;
    }
  }

  /**
   * Make an HTTP request with retry logic
   */
  protected async requestWithRetry<T>(
    endpoint: string,
    options: RequestInit = {},
    serviceURL?: string,
    retries: number = this.config.retries,
    requestOptions?: RequestOptions
  ): Promise<T> {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await this.request<T>(endpoint, options, serviceURL, requestOptions);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');

        // Don't retry on consumer abort
        if (requestOptions?.signal?.aborted) {
          throw lastError;
        }

        // Don't retry on client errors (4xx), except 429 (rate limit)
        if (
          lastError instanceof VesantError &&
          lastError.statusCode &&
          lastError.statusCode >= 400 &&
          lastError.statusCode < 500 &&
          lastError.statusCode !== 429
        ) {
          throw lastError;
        }

        // Don't retry on last attempt
        if (attempt === retries) {
          break;
        }

        // Use Retry-After for 429, otherwise exponential backoff with jitter
        let delay: number;
        if (lastError instanceof RateLimitError && lastError.retryAfter) {
          delay = lastError.retryAfter * 1000;
        } else {
          delay = Math.min(1000 * Math.pow(2, attempt) + Math.random() * 1000, 10000);
        }
        await new Promise((resolve) => setTimeout(resolve, delay));

        if (this.config.debug) {
          this.logger.debug(`Retry attempt ${attempt + 1}/${retries} after ${delay.toFixed(0)}ms`);
        }
      }
    }

    throw new NetworkError(`Request failed after ${retries} retries`, lastError);
  }

  /**
   * Handle error responses from API
   */
  protected handleErrorResponse(status: number, data: Record<string, unknown>, requestId?: string): never {
    const message = (data.error as string) || (data.message as string) || `HTTP ${status}`;

    const createError = (): VesantError => {
      switch (status) {
        case 400:
          return new VesantError(message, 'BAD_REQUEST', 400);
        case 401:
          return new AuthenticationError(message);
        case 403:
          return new VesantError(message, 'FORBIDDEN', 403);
        case 404:
          return new VesantError(message, 'NOT_FOUND', 404);
        case 409:
          return new VesantError(message, 'DUPLICATE_PROFILE', 409);
        case 429: {
          const retryAfter = (data.retry_after as number | undefined) || (data.retryAfter as number | undefined);
          return new RateLimitError(retryAfter);
        }
        case 500:
        case 502:
        case 503:
        case 504:
          return new ServiceUnavailableError(message);
        default:
          return new VesantError(message, 'UNKNOWN_ERROR', status);
      }
    };

    const error = createError();
    if (requestId) {
      error.requestId = requestId;
    }
    throw error;
  }

  /**
   * Build query string from parameters
   */
  protected buildQueryString(params: Record<string, unknown>): string {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((item) => query.append(key, String(item)));
        } else {
          query.append(key, String(value));
        }
      }
    });

    const queryString = query.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * Update client configuration
   */
  updateConfig(config: Partial<BaseClientConfig>): void {
    this.config = {
      ...this.config,
      ...config,
      headers: {
        ...this.config.headers,
        ...(config.headers || {}),
      },
    };
    if (config.logger) {
      this.logger = config.logger;
    }
    if (config.interceptors) {
      this.interceptors = config.interceptors;
    }
  }

  /**
   * Get current configuration (readonly)
   */
  getConfig(): Readonly<BaseClientConfig> {
    return { ...this.config };
  }

  /**
   * Get rate limit status from tracked response headers.
   */
  getRateLimitStatus(): RateLimitStatus | null {
    return this.rateLimitTracker?.getStatus() ?? null;
  }

  /**
   * Get circuit breaker status.
   */
  getCircuitBreakerStatus(): CircuitBreakerStatus | null {
    return this.circuitBreaker?.getStatus() ?? null;
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request('/api/v1/health');
  }
}
